import React, { useState } from 'react';
import Pagination from '../common/Pagination';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PrinterIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { proformaService } from '../../services/api';
import {
  FactureProformaList,
  FactureProforma,
  CreateProformaData,
  StatutProforma,
  ConvertProformaData,
} from '../../types';
import ProformaForm from './ProformaForm';
import ConvertProformaModal from './ConvertProformaModal';

const ProformaListComponent: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatut, setSelectedStatut] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingProforma, setEditingProforma] = useState<FactureProforma | null>(null);
  /** Bandeau de confirmation/erreur affiché au-dessus de la liste */
  const [feedback, setFeedback] = useState<
    { type: 'succes' | 'erreur'; message: string; proformaId?: number } | null
  >(null);
  /** Erreur remontée dans le modal, qui reste ouvert pour permettre la correction */
  const [erreurFormulaire, setErreurFormulaire] = useState<string | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingProforma, setConvertingProforma] = useState<FactureProforma | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch proformas
  const { data: proformasData, isLoading } = useQuery({
    queryKey: ['proformas', currentPage, searchTerm, selectedStatut],
    queryFn: () =>
      proformaService.getProformas({
        page: currentPage,
        search: searchTerm || undefined,
        status: selectedStatut || undefined,
      }),
  });

  // Fetch statuts
  const { data: statutsData } = useQuery({
    queryKey: ['proformas-statuts'],
    queryFn: () => proformaService.getStatuts(),
  });

  // Fetch statistiques
  const { data: statsData } = useQuery({
    queryKey: ['proformas-stats'],
    queryFn: () => proformaService.getStatistiques(),
  });

  const proformas = proformasData?.data?.results || [];
  const totalCount = proformasData?.data?.count || 0;
  const statuts = statutsData?.data || [];
  const stats = statsData?.data;

  /** Extrait un message lisible d'une erreur DRF (détail, champ, ou repli) */
  const messageErreur = (error: any, repli: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.detail) return data.detail;
    if (data?.error) return data.error;
    if (data && typeof data === 'object') {
      const premier = Object.entries(data)[0];
      if (premier) {
        const [champ, valeur] = premier;
        const texte = Array.isArray(valeur) ? valeur.join(' ') : String(valeur);
        return `${champ} : ${texte}`;
      }
    }
    return repli;
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateProformaData) => proformaService.createProforma(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
      queryClient.invalidateQueries({ queryKey: ['proformas-stats'] });
      setCurrentPage(1);
      setShowForm(false);
      setErreurFormulaire(null);
      setFeedback({
        type: 'succes',
        message: `Devis ${response.data.numero} créé avec ${response.data.lignes?.length ?? 0} prestation(s).`,
        proformaId: response.data.id,
      });
    },
    // Sans cette gestion, un échec laissait le modal ouvert sans le moindre
    // message : l'utilisateur croyait que le bouton ne faisait rien.
    onError: (error: any) =>
      setErreurFormulaire(messageErreur(error, "La création du devis a échoué.")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateProformaData> }) =>
      proformaService.updateProforma(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
      queryClient.invalidateQueries({ queryKey: ['proformas-stats'] });
      setEditingProforma(null);
      setShowForm(false);
      setErreurFormulaire(null);
      setFeedback({
        type: 'succes',
        message: `Devis ${response.data.numero} mis à jour — ${response.data.lignes?.length ?? 0} prestation(s).`,
        proformaId: response.data.id,
      });
    },
    onError: (error: any) =>
      setErreurFormulaire(messageErreur(error, "La modification du devis a échoué.")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => proformaService.deleteProforma(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
      queryClient.invalidateQueries({ queryKey: ['proformas-stats'] });
    },
  });

  const envoyerMutation = useMutation({
    mutationFn: (id: number) => proformaService.envoyerProforma(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
    },
  });

  const accepterMutation = useMutation({
    mutationFn: (id: number) => proformaService.accepterProforma(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
      queryClient.invalidateQueries({ queryKey: ['proformas-stats'] });
    },
  });

  const convertirMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ConvertProformaData }) =>
      proformaService.convertirProforma(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
      queryClient.invalidateQueries({ queryKey: ['proformas-stats'] });
      queryClient.invalidateQueries({ queryKey: ['factures'] });
      setShowConvertModal(false);
      setConvertingProforma(null);
      alert(`Facture ${response.data.facture_numero} créée avec succès!`);
    },
  });

  const annulerMutation = useMutation({
    mutationFn: (id: number) => proformaService.annulerProforma(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
      queryClient.invalidateQueries({ queryKey: ['proformas-stats'] });
    },
  });

  const dupliquerMutation = useMutation({
    mutationFn: (id: number) => proformaService.dupliquerProforma(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] });
    },
  });

  const handleSubmit = (data: CreateProformaData) => {
    setErreurFormulaire(null);
    if (editingProforma) {
      updateMutation.mutate({ id: editingProforma.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = async (proforma: FactureProformaList) => {
    try {
      const response = await proformaService.getProforma(proforma.id);
      setEditingProforma(response.data);
      setErreurFormulaire(null);
      setShowForm(true);
    } catch (error) {
      setFeedback({ type: 'erreur', message: messageErreur(error, "Impossible d'ouvrir ce devis.") });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Voulez-vous vraiment supprimer ce devis?')) {
      deleteMutation.mutate(id);
    }
  };

  const handlePrint = async (id: number) => {
    try {
      const response = await proformaService.imprimerProforma(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const onglet = window.open(url, '_blank');
      if (!onglet) {
        setFeedback({
          type: 'erreur',
          message: "L'ouverture du PDF a été bloquée par le navigateur : autorisez les fenêtres surgissantes.",
        });
      }
      // Libérer l'URL une fois l'onglet chargé
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      setFeedback({ type: 'erreur', message: messageErreur(error, "L'impression du devis a échoué.") });
    }
  };

  const handleConvert = async (proforma: FactureProformaList) => {
    try {
      const response = await proformaService.getProforma(proforma.id);
      setConvertingProforma(response.data);
      setShowConvertModal(true);
    } catch (error) {
      setFeedback({ type: 'erreur', message: messageErreur(error, "Impossible d'ouvrir ce devis.") });
    }
  };

  const handleConvertSubmit = (data: ConvertProformaData) => {
    if (convertingProforma) {
      convertirMutation.mutate({ id: convertingProforma.id, data });
    }
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  const getStatutBadge = (statut: StatutProforma) => {
    const badges: Record<StatutProforma, { bg: string; text: string; label: string }> = {
      brouillon: { bg: 'son-chip-gray', text: '', label: 'Brouillon' },
      envoyee: { bg: 'son-chip-blue', text: '', label: 'Envoyée' },
      acceptee: { bg: 'son-chip-green', text: '', label: 'Acceptée' },
      refusee: { bg: 'son-chip-red', text: '', label: 'Refusée' },
      convertie: { bg: 'son-chip-purple', text: '', label: 'Convertie' },
      expiree: { bg: 'son-chip-amber', text: '', label: 'Expirée' },
      annulee: { bg: 'son-chip-gray', text: 'opacity-80', label: 'Annulée' },
    };
    const badge = badges[statut] || badges.brouillon;
    return (
      <span className={`son-chip ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      {/* Retour après création / modification / erreur */}
      {feedback && (
        <div
          className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3 ${
            feedback.type === 'succes'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-start gap-2">
            {feedback.type === 'succes' ? (
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-sm">{feedback.message}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {feedback.proformaId && (
              <button
                onClick={() => handlePrint(feedback.proformaId!)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 text-emerald-800 text-sm rounded-lg hover:bg-emerald-100"
              >
                <PrinterIcon className="h-4 w-4" /> Imprimer
              </button>
            )}
            <button
              onClick={() => setFeedback(null)}
              className="text-current opacity-60 hover:opacity-100 text-sm"
              aria-label="Fermer le message"
            >
              <XCircleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="son-kpi !block">
            <p className="son-kpi-label">Total ce mois</p>
            <p className="son-kpi-value">{stats.proformas_mois}</p>
            <p className="text-[11.5px] text-[color:var(--muted)] tabular-nums">{formatMontant(stats.montant_total_mois)} GNF</p>
          </div>
          <div className="son-kpi !block">
            <p className="son-kpi-label">En attente</p>
            <p className="son-kpi-value !text-[#B5781A]">{stats.en_attente}</p>
          </div>
          <div className="son-kpi !block">
            <p className="son-kpi-label">Converties</p>
            <p className="son-kpi-value !text-[#0E8466]">{stats.converties}</p>
          </div>
          <div className="son-kpi !block">
            <p className="son-kpi-label">Taux de conversion</p>
            <p className="son-kpi-value !text-[#1565B8]">{stats.taux_conversion.toFixed(0)}%</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="son-page-head">
        <div>
          <h2 className="son-h1">Factures Pro Forma (Devis)</h2>
          <p className="son-sub">{totalCount} devis · validité et conversion en facture</p>
        </div>
        <button
          onClick={() => {
            setEditingProforma(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 shadow-sm transition-colors"
        >
          <PlusIcon className="h-4 w-4" aria-hidden />
          Nouveau devis
        </button>
      </div>

      {/* Filters */}
      <div className="son-toolbar">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="son-search">
            <MagnifyingGlassIcon aria-hidden />
            <input
              type="search"
              aria-label="Rechercher un devis"
              placeholder="N° de devis, patient, objet…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input text-gray-700"
            />
          </div>

          <select
            value={selectedStatut}
            onChange={(e) => setSelectedStatut(e.target.value)}
            aria-label="Filtrer par statut"
            className="form-select text-gray-700 md:w-56"
          >
            <option value="">Tous les statuts</option>
            {statuts.map((s: { value: string; label: string }) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedStatut('');
            }}
            className="btn-secondary"
          >
            <ArrowPathIcon className="h-4 w-4" aria-hidden />
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="son-table-wrap">
        {isLoading ? (
          <div className="p-4 space-y-3" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="son-skeleton h-9" />)}
          </div>
        ) : proformas.length === 0 ? (
          <div className="son-empty">
            <div className="son-empty-icon"><DocumentDuplicateIcon className="h-6 w-6" aria-hidden /></div>
            <p className="font-semibold text-[color:var(--text)]">Aucun devis trouvé</p>
            <p>Créez un devis pour estimer un plan de traitement.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="son-table">
            <thead>
              <tr>
                <th>N° / Patient</th>
                <th>Objet</th>
                <th className="!text-right">Montant</th>
                <th>Validité</th>
                <th>Statut</th>
                <th className="!text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {proformas.map((proforma: FactureProformaList) => (
                <tr key={proforma.id} className={!proforma.est_valide && proforma.status !== 'convertie' ? '!bg-[#FFF8EC]' : ''}>
                  <td>
                    <div className="font-bold tabular-nums">{proforma.numero}</div>
                    <button
                      type="button"
                      className="text-[12.5px] text-[color:var(--muted)] hover:text-[color:var(--blue)] hover:underline text-left"
                      onClick={() => navigate(`/patients/${proforma.patient}`)}
                      title="Voir le dossier patient"
                    >
                      {proforma.patient_nom}
                    </button>
                  </td>
                  <td>
                    <div className="max-w-xs truncate" title={proforma.objet || undefined}>{proforma.objet || '-'}</div>
                  </td>
                  <td>
                    <div className="font-bold text-right tabular-nums whitespace-nowrap">
                      {formatMontant(proforma.montant_patient)} GNF
                    </div>
                  </td>
                  <td>
                    <div className="tabular-nums whitespace-nowrap">
                      {new Date(proforma.date_validite).toLocaleDateString('fr-FR')}
                    </div>
                    {proforma.jours_restants !== null && proforma.jours_restants !== undefined && (
                      <div className={`text-xs ${proforma.jours_restants < 0 ? 'text-[#C0392B] font-semibold' : proforma.jours_restants < 7 ? 'text-[#B5781A] font-semibold' : 'text-[color:var(--muted)]'}`}>
                        {proforma.jours_restants < 0 ? (
                          <>
                            <ExclamationTriangleIcon className="h-3 w-3 inline mr-1" />
                            Expiré
                          </>
                        ) : (
                          <>
                            <ClockIcon className="h-3 w-3 inline mr-1" />
                            {proforma.jours_restants} jours
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {getStatutBadge(proforma.status)}
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="flex justify-end gap-0.5">
                      {/* Imprimer */}
                      <button
                        onClick={() => handlePrint(proforma.id)}
                        className="son-icon-btn"
                        title="Imprimer"
                        aria-label="Imprimer"
                      >
                        <PrinterIcon className="h-4 w-4" />
                      </button>

                      {proforma.status === 'brouillon' && (
                        <>
                          <button
                            onClick={() => envoyerMutation.mutate(proforma.id)}
                            className="son-icon-btn is-blue"
                            title="Marquer comme envoyée"
                            aria-label="Marquer comme envoyée"
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(proforma)}
                            className="son-icon-btn"
                            title="Modifier"
                            aria-label="Modifier"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}

                      {(proforma.status === 'brouillon' || proforma.status === 'envoyee') && (
                        <button
                          onClick={() => accepterMutation.mutate(proforma.id)}
                          className="son-icon-btn is-teal"
                          title="Marquer comme acceptée"
                          aria-label="Marquer comme acceptée"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                      )}

                      {proforma.est_valide && proforma.status !== 'convertie' && proforma.status !== 'annulee' && (
                        <button
                          onClick={() => handleConvert(proforma)}
                          className="son-icon-btn is-purple"
                          title="Convertir en facture"
                          aria-label="Convertir en facture"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                      )}

                      {/* Dupliquer */}
                      <button
                        onClick={() => dupliquerMutation.mutate(proforma.id)}
                        className="son-icon-btn"
                        title="Dupliquer"
                        aria-label="Dupliquer"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                      </button>

                      {proforma.status !== 'convertie' && proforma.status !== 'annulee' && (
                        <button
                          onClick={() => annulerMutation.mutate(proforma.id)}
                          className="son-icon-btn is-amber"
                          title="Annuler"
                          aria-label="Annuler"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      )}

                      {proforma.status === 'brouillon' && (
                        <button
                          onClick={() => handleDelete(proforma.id)}
                          className="son-icon-btn is-red"
                          title="Supprimer"
                          aria-label="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        <Pagination page={currentPage} pageSize={20} count={totalCount} label="devis" onChange={setCurrentPage} />
      </div>

      {/* Form Modal */}
      {showForm && (
        <ProformaForm
          error={erreurFormulaire}
          proforma={editingProforma}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingProforma(null);
            setErreurFormulaire(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Convert Modal */}
      {showConvertModal && convertingProforma && (
        <ConvertProformaModal
          proforma={convertingProforma}
          onSubmit={handleConvertSubmit}
          onClose={() => {
            setShowConvertModal(false);
            setConvertingProforma(null);
          }}
          isLoading={convertirMutation.isPending}
        />
      )}
    </div>
  );
};

export default ProformaListComponent;
